import { Typography } from '@mui/material';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import {
  CrudTable,
  DataGridColumnDefinition,
  DateLabel,
  StylizedNumber,
} from 'components';
import { TranslationNamespace } from 'i18n';
import { PayinNotAssignedReasonRow, PayinNotAssignedReasons } from 'types';
import { formatUtils, payinProviderStatisticsUtils } from 'utils';

type Props = {
  queryResult: UseQueryResult<PayinNotAssignedReasons>;
};

const EMPTY_VALUE = '—';

export const NotAssignedReasonsTable: React.FC<Props> = ({ queryResult }) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion.not_assigned',
  });

  const rowsQueryResult = useMemo(
    () =>
      ({
        ...queryResult,
        data: queryResult.data?.items,
      } as UseQueryResult<PayinNotAssignedReasonRow[]>),
    [queryResult],
  );

  const columns = useMemo(
    (): DataGridColumnDefinition<PayinNotAssignedReasonRow>[] => [
      {
        header: t('columns.reason'),
        valueGetter: (item) =>
          payinProviderStatisticsUtils.getNotAssignedReasonLabel(item.code),
      },
      {
        header: t('columns.p2p_provider'),
        valueGetter: (item) => item.providerName || EMPTY_VALUE,
      },
      {
        header: t('columns.count'),
        valueGetter: (item) => (
          <StylizedNumber value={item.count} unit={tCommon('units.items')} />
        ),
      },
      {
        header: t('columns.share'),
        valueGetter: (item) => <StylizedNumber value={item.share} unit="%" />,
      },
      {
        header: t('columns.period'),
        valueGetter: (item) => (
          <div>
            <DateLabel>{formatUtils.formatDate(item.firstAt)}</DateLabel>
            <DateLabel>{formatUtils.formatDate(item.lastAt)}</DateLabel>
          </div>
        ),
      },
    ],
    [t, tCommon],
  );

  return (
    <Fragment>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        {t('hint')}
      </Typography>
      <CrudTable queryResult={rowsQueryResult} columns={columns} />
    </Fragment>
  );
};
